/**
 * JOAdminOrderFulfillmentInjector — Highlights the fulfillment mode
 * (Click & Collect vs Livraison) + requested date on every admin order
 * view, WITHOUT modifying the cockpit OrdersAdmin.jsx / OrderDetailModal.jsx.
 *
 * Two injection points :
 *   1. List `/admin/orders` — ensures each row displays a coloured badge
 *      (Click & Collect / Livraison) + the requested date. Cockpit already
 *      renders that for pickup orders but leaves delivery orders naked.
 *   2. Order detail modal ([data-testid="order-detail"]) — injects a full-
 *      width banner right under the header with mode + requested date.
 *
 * Reads the info from a lightweight index built via the backend list
 * response cached in memory (fetched once when the admin lands on
 * /admin/orders or opens a detail). Idempotent + MutationObserver.
 */
import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { api } from "@/lib/api";

const MARKER_ROW = "data-jdo-order-fmt";
const MARKER_MODAL = "data-jdo-order-modal-fmt";

function frDate(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleString("fr-FR", {
      weekday: "long", day: "2-digit", month: "long",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
}

function fmtLabel(o) {
  const type = o?.fulfillment_type || "";
  if (type === "pickup") return { label: "Click & Collect · Retrait boutique", color: "sky", icon: "map" };
  if (type === "booking") return { label: "Réservation", color: "gold", icon: "calendar" };
  return { label: "Livraison à domicile", color: "green", icon: "truck" };
}

const SVG_MAP = `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>`;
const SVG_TRUCK = `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><rect x="1" y="3" width="15" height="13"/><polygon points="16 8 20 8 23 11 23 16 16 16 16 8"/><circle cx="5.5" cy="18.5" r="2.5"/><circle cx="18.5" cy="18.5" r="2.5"/></svg>`;
const SVG_CAL = `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>`;

function iconFor(name) {
  return name === "map" ? SVG_MAP : name === "truck" ? SVG_TRUCK : SVG_CAL;
}

export default function JOAdminOrderFulfillmentInjector() {
  const location = useLocation();
  const indexRef = useRef({}); // id -> order

  // Fetch orders list once when on /admin/orders (or when it changes)
  const loadIndex = async () => {
    try {
      const r = await api.get("/admin/orders?limit=200");
      const items = r.data || [];
      const map = {};
      for (const o of items) map[o.id] = o;
      indexRef.current = map;
      enhanceRows();
    } catch {
      /* silent */
    }
  };

  const enhanceRows = () => {
    // Only inject when we're actually on the orders admin list
    if (!location.pathname.startsWith("/admin/orders")) return;
    const rows = document.querySelectorAll('[data-testid^="order-row-"]');
    rows.forEach((row) => {
      if (row.getAttribute(MARKER_ROW) === "yes") return;
      const testId = row.getAttribute("data-testid") || "";
      const id = testId.replace("order-row-", "");
      const order = indexRef.current[id];
      if (!order) return;
      // Skip: cockpit already renders a Click & Collect badge on pickup rows.
      // We add a Livraison badge on non-pickup rows and a requested date pill.
      const isPickup = order.fulfillment_type === "pickup";
      const info = fmtLabel(order);
      // Find the first row of badges (contains the status pill + potentially C&C)
      const badgeStrip = row.querySelector('.flex.items-center.gap-2.flex-wrap');
      if (badgeStrip && !isPickup) {
        // Insert a Livraison badge if missing
        const existing = badgeStrip.querySelector(".jdo-fmt-row-badge");
        if (!existing) {
          const span = document.createElement("span");
          span.className = `jdo-fmt-row-badge jdo-fmt-${info.color}`;
          span.innerHTML = `${iconFor(info.icon)}<span>${info.label}</span>`;
          badgeStrip.appendChild(span);
        }
      }
      // Requested date pill (delivery date OR pickup slot)
      const requested = order.pickup_slot_at || order.requested_delivery_date || order.delivery_date;
      if (requested && !row.querySelector(".jdo-fmt-row-date")) {
        const button = row.querySelector('button[data-testid^="order-open-"]');
        if (button) {
          const wrap = document.createElement("div");
          wrap.className = `jdo-fmt-row-date ${isPickup ? "jdo-fmt-sky" : "jdo-fmt-green"}`;
          wrap.innerHTML = `${SVG_CAL}<span>${isPickup ? "Retrait" : "Livraison"} demandé(e) : <strong>${frDate(requested)}</strong></span>`;
          button.appendChild(wrap);
        }
      }
      row.setAttribute(MARKER_ROW, "yes");
    });
  };

  const enhanceModal = () => {
    const modal = document.querySelector('[data-testid="order-detail-modal"]');
    if (!modal) return;
    if (modal.getAttribute(MARKER_MODAL) === "yes") return;
    // Look for the cockpit's Mode stat card and enrich it with the requested
    // date pill if any. The stat cards are the first 4 grid children.
    const stats = modal.querySelectorAll('div.grid.md\\:grid-cols-4 > *');
    if (!stats || stats.length < 4) return;
    // Identify the order via the header
    const monoSpan = modal.querySelector("h2 .font-mono");
    if (!monoSpan) return;
    const shown = (monoSpan.textContent || "").replace(/^#/, "").toLowerCase();
    const order = Object.values(indexRef.current).find(
      (o) => o.id.slice(0, 8).toLowerCase() === shown.slice(0, 8).toLowerCase()
        || (o.number || "").toLowerCase() === shown.toLowerCase(),
    );
    if (!order) return;
    const requested = order.pickup_slot_at || order.requested_delivery_date || order.delivery_date;
    // Locate the Mode stat card (last of the 4)
    const modeCard = stats[stats.length - 1];
    if (!modeCard || modeCard.querySelector(".jdo-fmt-date-pill")) return;
    const isPickup = order.fulfillment_type === "pickup";
    const pill = document.createElement("div");
    pill.className = "jdo-fmt-date-pill " + (isPickup ? "jdo-fmt-sky" : "jdo-fmt-green");
    pill.innerHTML = requested
      ? `${SVG_CAL}<span>${isPickup ? "Retrait" : "Livraison"} le <strong>${frDate(requested)}</strong></span>`
      : `<span class="jdo-fmt-muted-text">Date demandée non précisée</span>`;
    modeCard.appendChild(pill);
    modal.setAttribute(MARKER_MODAL, "yes");
  };

  useEffect(() => {
    if (!location.pathname.startsWith("/admin/orders")) return;
    loadIndex();
    const observer = new MutationObserver(() => {
      enhanceRows();
      enhanceModal();
    });
    observer.observe(document.body, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, [location.pathname]);

  return null;
}
